import pinia from '@/plugins/pinia';
import {useApplicationState} from '@/store/applicationState';
import {StorageStore} from '@/store/types';
import {getCurrentInstance, onMounted, computed} from 'vue';
import {RouteLocationNormalizedLoaded as Route, useRoute} from 'vue-router';

// This is a drop-in composable replacement of the mixin version.

export function makeUseStorage({doFetch = true}: {doFetch?: boolean} = {}) {
  return <FilterType, SortType, DataType>(
    store: StorageStore<FilterType, SortType, DataType>,
    titleGetter: ((
      data: DataType | null /* (typeof store.data)*/,
      route?: Route
    ) => string | null) | undefined,
    filterDeriver:
    | ((
        route: Route,
        originalFilters: FilterType, /* (typeof store.filters)*/
        defaultFilters: FilterType /* (typeof store.filters)*/
      ) => FilterType /* (typeof store.filters)*/)
    | null = null,
    routeEntered: ((to: Route, from: Route) => void) | null = null,
  ) => {
    if (!store) throw new Error('store not defined');
    const dataFilters = computed(
        (): (typeof store.filters) => {
          return store.filters;
        },
    );
    onMounted(async () => {
      const instance = getCurrentInstance();
      const applicationState = useApplicationState(pinia);
      store.loading = true;
      let derivedFilters:
      | (typeof store.filters)
      | null = null;
      const route = instance?.proxy?.$route;
      if (route && filterDeriver) {
        derivedFilters = filterDeriver(
            route,
            store.filters,
            store.defaultFilters,
        );
      }
      if (derivedFilters) {
        store.filters = derivedFilters;
      }

      if (doFetch) {
        await store.fetch();
      }
      if (titleGetter) {
        applicationState.updateTitle(
            titleGetter(store.data, instance?.proxy?.$route),
        );
      };
      if (routeEntered) {
        const route = useRoute();
        routeEntered(route, route);
      }
    });
    async function reloadStorageData() {
      try {
        if (store) {
          await store.fetch();
        }
      } catch {}
    }

    return {
      dataFilters,
      reloadStorageData,
    };
  };
}

export const useStorage = makeUseStorage({doFetch: true});
export const useStorageRaw = makeUseStorage({doFetch: false});
